import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, X, UserPlus, Calendar, Phone, Mail, AlertCircle, Target, Loader2, Printer, Link2, BarChart3, Send, CreditCard, Wallet, Clock, TrendingUp, ShieldCheck } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import { PUBLIC_HOST } from "../lib/domain";

export default function PendingInscriptionsModule() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(null);
  const [view, setView] = useState("pending");
  const [processed, setProcessed] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pList, pStats, proc] = await Promise.all([
        api.get("/inscriptions/pending"),
        api.get("/inscriptions/stats"),
        api.get("/inscriptions/processed"),
      ]);
      setList(pList.data);
      setStats(pStats.data);
      setProcessed(proc.data.processed || []);
    } catch { setList([]); setStats(null); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const relance = async (id, name) => {
    try {
      const { data } = await api.post(`/inscriptions/${id}/relance`);
      const via = [data.sent?.email && "courriel", data.sent?.sms && "SMS"].filter(Boolean).join(" + ");
      toast.success(`Relance envoyée à ${name}${via ? ` (${via})` : ""}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible"); }
  };

  const reject = async (id, name) => {
    if (!window.confirm(`Rejeter l'inscription de ${name} ?`)) return;
    try {
      await api.post(`/inscriptions/${id}/reject`);
      toast.success("Inscription rejetée");
      load();
    } catch { toast.error("Erreur"); }
  };

  const downloadPoster = async () => {
    try {
      const r = await api.get("/inscriptions/poster.pdf", { responseType: "blob" });
      const blob = r.data;
      if (blob.type !== "application/pdf") throw new Error("PDF error");
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "poster-inscription-institut-impact.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Poster A4 téléchargé");
    } catch { toast.error("Erreur PDF"); }
  };

  const sendWelcomeTest = async () => {
    const t = toast.loading("Envoi de l'aperçu…");
    try {
      const { data } = await api.post("/stripe/member/welcome-email/test");
      toast.dismiss(t);
      toast.success(data.message || "Aperçu envoyé.");
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    }
  };

  const checkWebhook = async () => {
    const t = toast.loading("Vérification du webhook Stripe…");
    try {
      const { data } = await api.get("/stripe/webhook-health");
      toast.dismiss(t);
      const icon = data.status === "ok" ? "✅" : data.status === "warn" ? "⚠️" : "🔴";
      const desc = data.last_event
        ? `Dernier événement : ${data.last_event.type} · ${(data.last_event.at || "").slice(0, 16).replace("T", " ")}`
        : "Aucun événement reçu pour l'instant.";
      if (data.status === "ok") toast.success(`${icon} ${data.message}`, { description: desc });
      else if (data.status === "warn") toast.warning(`${icon} ${data.message}`, { description: desc });
      else toast.error(`${icon} ${data.message}`, { description: desc });
    } catch (e) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.detail || "Vérification impossible");
    }
  };

  return (
    <div data-testid="pending-inscriptions-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Inscriptions en attente"
          icon={UserPlus}
          breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Inscriptions" }]}
          subtitle="Approuve les nouveaux élèves qui se sont inscrits via la page publique."
          actions={
            <span className="bg-impact-red text-white px-3 py-1.5 rounded-lg text-sm font-bold" data-testid="count-badge">{list.length} en attente</span>
          }
        />
        <div className="mb-6 flex flex-wrap items-center gap-3 text-xs">
          <span className="uppercase tracking-[0.15em] text-zinc-500">URL publique d'inscription :</span>
          <code className="px-2 py-1 bg-zinc-800 text-amber-300 rounded" data-testid="public-url">{window.location.origin}/inscription</code>
          <button data-testid="poster-btn" onClick={downloadPoster} className="bg-amber-500 text-black hover:bg-amber-400 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors">
            <Printer size={12}/> POSTER A4 RÉCEPTION
          </button>
          <ShareLinks origin={window.location.origin}/>
          <button data-testid="check-webhook-btn" onClick={checkWebhook} className="bg-zinc-800 text-emerald-300 border border-emerald-700 hover:bg-zinc-700 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors">
            <ShieldCheck size={12}/> VÉRIFIER LE WEBHOOK
          </button>
          <button data-testid="test-welcome-email-btn" onClick={sendWelcomeTest} className="bg-zinc-800 text-sky-300 border border-sky-800 hover:bg-zinc-700 text-xs px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition-colors">
            <Mail size={12}/> APERÇU REÇU DE BIENVENUE
          </button>
        </div>
      </div>

      <div className="px-8 lg:px-12 py-6">
        {stats && <ConversionStats stats={stats}/>}
        <HotAlerts/>
        {stats && stats.total > 0 && <SourceStats stats={stats}/>}

        <div className="flex items-center gap-2 mb-5 mt-2" data-testid="inscriptions-tabs">
          <button data-testid="tab-pending" onClick={() => setView("pending")}
            className={`ticker text-xs px-4 py-2 rounded-lg transition-colors ${view === "pending" ? "bg-impact-gold text-black font-bold" : "hard-border hover:bg-secondary"}`}>
            En attente ({list.length})
          </button>
          <button data-testid="tab-processed" onClick={() => setView("processed")}
            className={`ticker text-xs px-4 py-2 rounded-lg transition-colors ${view === "processed" ? "bg-impact-gold text-black font-bold" : "hard-border hover:bg-secondary"}`}>
            Nouveaux membres ({processed.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="animate-spin mx-auto"/></div>
        ) : view === "processed" ? (
          processed.length === 0 ? (
            <div className="text-center py-16 text-muted-foreground"><Check size={48} className="mx-auto mb-3 opacity-50"/><p>Aucune inscription traitée pour l'instant.</p></div>
          ) : (
            <div className="space-y-2" data-testid="processed-list">
              {(() => { const sans = processed.filter((p) => !p.a_carnet).length; return (
                <div className="hard-border p-3 mb-3 bg-secondary/40 text-xs flex items-start gap-2" data-testid="new-members-reco">
                  <Target size={14} className="mt-0.5 text-impact-gold"/>
                  <div>
                    <b>Suivi des nouveaux membres.</b> Chaque membre inscrit via {PUBLIC_HOST} doit avoir un <b>dossier (carnet)</b> pour suivre sa progression.
                    {sans > 0
                      ? <> ⚠️ <b>{sans} membre(s) sans dossier</b> — clique « Créer le dossier » pour ne rien oublier.</>
                      : <> ✅ Tous tes nouveaux membres ont un dossier. Pense à saisir leur 1<sup>re</sup> évaluation.</>}
                  </div>
                </div>
              ); })()}
              {processed.map((p) => (
                <article key={p.id} data-testid={`processed-${p.id}`} className="hard-border p-4 flex items-center justify-between gap-4 flex-wrap">
                  <div>
                    <div className="font-display font-bold">{p.full_name}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{(p.discipline || "—").replace(/_/g, " ")} · {(p.groupe_age || "—").replace(/_/g, " ")} · approuvé {p.approved_at}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    {p.a_carnet
                      ? <a data-testid={`view-dossier-${p.id}`} href={`/carnet/${p.carnet_link}`} target="_blank" rel="noreferrer" className="ticker text-[10px] bg-sky-500/15 text-sky-500 px-2.5 py-1.5 rounded flex items-center gap-1 hover:bg-sky-500/25"><Check size={11}/> Voir le dossier</a>
                      : <a data-testid={`create-dossier-${p.id}`} href="/app/audit-rentree" className="ticker text-[10px] bg-amber-500/15 text-amber-500 px-2.5 py-1.5 rounded flex items-center gap-1 hover:bg-amber-500/25"><AlertCircle size={11}/> Créer le dossier</a>}
                  </div>
                </article>
              ))}
            </div>
          )
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <UserPlus size={48} className="mx-auto mb-3 opacity-50"/>
            <p>Aucune inscription en attente.</p>
            <p className="text-xs mt-2">Partage ton URL <code className="bg-secondary px-1">{window.location.origin}/inscription</code> sur ton site / Facebook / Instagram.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {list.map((i) => (
              <ApproveCard key={i.id} inscription={i} onApprove={() => setApproving(i)} onReject={() => reject(i.id, i.full_name)} onRelance={() => relance(i.id, i.full_name)}/>
            ))}
          </div>
        )}
      </div>

      {approving && (
        <ApproveDrawer inscription={approving} onClose={() => setApproving(null)} onSaved={() => { setApproving(null); load(); }}/>
      )}
    </div>
  );
}

function ApproveCard({ inscription, onApprove, onReject, onRelance }) {
  const i = inscription;
  return (
    <article className="hard-border p-5 grid grid-cols-12 gap-4" data-testid={`pi-${i.id}`}>
      <div className="col-span-3">
        <div className="font-display font-bold text-xl">{i.full_name}</div>
        <div className="ticker text-xs text-muted-foreground mt-1">{i.discipline?.replace(/_/g, " ").toUpperCase()}</div>
        <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Calendar size={10}/> reçu {i.submitted_at?.slice(0, 10)}</div>
        {i.is_minor && <div className="ticker text-[10px] bg-impact-red/15 text-impact-red px-1.5 py-0.5 inline-block mt-1">MINEUR</div>}
        {i.members?.length > 0 && (
          <div className="ticker text-[10px] bg-impact-gold/15 text-impact-gold px-1.5 py-0.5 inline-block mt-1" data-testid={`family-badge-${i.id}`}>
            👨‍👩‍👧 FOYER · {i.members.length + 1} MEMBRES{i.household_savings > 0 ? ` · −${i.household_savings.toFixed(2)}$` : ""}
          </div>
        )}
      </div>
      <div className="col-span-4 text-sm space-y-1">
        {i.email && <div className="flex items-center gap-1.5"><Mail size={12} className="text-muted-foreground"/> <a href={`mailto:${i.email}`} className="hover:text-impact-red">{i.email}</a></div>}
        {i.phone && <div className="flex items-center gap-1.5"><Phone size={12} className="text-muted-foreground"/> <a href={`tel:${i.phone}`} className="hover:text-impact-red">{i.phone}</a></div>}
        {i.date_of_birth && <div className="text-xs text-muted-foreground">Né(e) : {i.date_of_birth}</div>}
        {i.is_minor && i.parent_name && <div className="text-xs mt-2 pt-2 border-t">Parent: <strong>{i.parent_name}</strong> · {i.parent_phone || i.parent_email}</div>}
      </div>
      <div className="col-span-3 text-sm">
        {i.niveau_actuel && <div className="mb-1"><span className="ticker text-[10px] text-muted-foreground">NIVEAU:</span> {i.niveau_actuel}</div>}
        {i.objectifs && <div className="text-xs italic text-muted-foreground line-clamp-3"><Target size={10} className="inline mr-1"/>{i.objectifs}</div>}
        {i.antecedents_medicaux && (
          <div className="text-xs bg-impact-red/5 border-l-2 border-impact-red pl-2 py-1 mt-2"><AlertCircle size={10} className="inline text-impact-red mr-1"/> {i.antecedents_medicaux}</div>
        )}
      </div>
      <div className="col-span-2 flex flex-col gap-2 items-end">
        <button data-testid={`approve-${i.id}`} onClick={onApprove}
          className="bg-impact-gold text-black font-bold ticker px-4 py-2 hover:bg-foreground hover:text-white transition-colors w-full flex items-center justify-center gap-1">
          <Check size={14}/> Approuver
        </button>
        <button data-testid={`reject-${i.id}`} onClick={onReject}
          className="hard-border ticker text-xs px-3 py-1.5 hover:bg-impact-red/10 hover:text-impact-red flex items-center gap-1">
          <X size={12}/> Rejeter
        </button>
        <button data-testid={`relance-${i.id}`} onClick={onRelance}
          className="ticker text-xs px-3 py-1.5 text-sky-500 hover:bg-sky-500/10 flex items-center gap-1">
          <Mail size={12}/> Relancer
        </button>
      </div>
    </article>
  );
}

function ApproveDrawer({ inscription, onClose, onSaved }) {
  const [tarif, setTarif] = useState(120);
  const [ceinture, setCeinture] = useState(inscription.niveau_actuel || "");
  const [groupe, setGroupe] = useState(inscription.is_minor ? "kids_8_12" : "adultes_18_plus");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/inscriptions/${inscription.id}/approve`, {
        tarif_mensuel: tarif,
        niveau_ceinture: ceinture,
        groupe_age: groupe,
      });
      toast.success(`${inscription.full_name} ajouté à tes contrats !`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="approve-drawer">
      <div className="absolute inset-0 bg-black/60" onClick={onClose}/>
      <div className="ml-auto w-full max-w-md bg-white h-full overflow-y-auto relative">
        <div className="hard-border-b p-4 sticky top-0 bg-white flex items-center justify-between">
          <h3 className="font-display font-bold text-lg">Approuver {inscription.full_name}</h3>
          <button onClick={onClose}><X size={18}/></button>
        </div>
        <div className="p-4 space-y-4">
          <div>
            <label className="ticker text-xs text-muted-foreground block mb-1">TARIF MENSUEL (CAD)</label>
            <input data-testid="ad-tarif" type="number" step="5" value={tarif} onChange={(e) => setTarif(parseFloat(e.target.value) || 0)} className="hard-border px-3 py-2 w-full text-2xl font-display"/>
          </div>
          <div>
            <label className="ticker text-xs text-muted-foreground block mb-1">CEINTURE DE DÉPART</label>
            <input data-testid="ad-ceinture" value={ceinture} onChange={(e) => setCeinture(e.target.value)} placeholder="blanche, jaune, ..." className="hard-border px-3 py-2 w-full"/>
          </div>
          <div>
            <label className="ticker text-xs text-muted-foreground block mb-1">GROUPE D'ÂGE</label>
            <select data-testid="ad-groupe" value={groupe} onChange={(e) => setGroupe(e.target.value)} className="hard-border px-3 py-2 w-full bg-white">
              <option value="kids_4_7">Parent-Enfant (3-6 ans)</option>
              <option value="kids_8_12">Enfant (8-12 ans)</option>
              <option value="ados_13_17">Adolescent (13-17 ans)</option>
              <option value="adultes_18_plus">Adulte (18+)</option>
              <option value="mixte">Mixte (tous âges)</option>
            </select>
          </div>
          <button data-testid="ad-submit" onClick={submit} disabled={saving}
            className="w-full bg-impact-gold text-black font-bold ticker py-3 hover:bg-foreground hover:text-white transition-colors disabled:opacity-50">
            {saving ? "Création..." : `Créer le contrat de ${inscription.full_name.split(" ")[0]}`}
          </button>
        </div>
      </div>
    </div>
  );
}


function ShareLinks({ origin }) {
  const sources = [
    { key: "site-web", label: "Site web", color: "bg-blue-500/15 text-blue-700" },
    { key: "facebook", label: "Facebook", color: "bg-blue-700/15 text-blue-800" },
    { key: "instagram", label: "Instagram", color: "bg-pink-500/15 text-pink-700" },
    { key: "qr-recep", label: "QR Réception", color: "bg-impact-gold/15 text-impact-gold" },
    { key: "qr-site", label: "QR Site Web", color: "bg-impact-gold/15 text-impact-gold" },
    { key: "google", label: "Google", color: "bg-green-500/15 text-green-700" },
    { key: "ami", label: "Référence ami", color: "bg-purple-500/15 text-purple-700" },
  ];
  const copyUrl = (src) => {
    const url = `${origin}/inscription?source=${src}`;
    navigator.clipboard.writeText(url).then(() => toast.success(`URL "${src}" copiée — colle dans ton post`));
  };
  return (
    <div className="mt-4 pt-4 border-t flex flex-wrap items-center gap-2">
      <span className="ticker text-xs text-muted-foreground flex items-center gap-1"><Link2 size={12}/> COPIER LIEN TRACKING :</span>
      {sources.map((s) => (
        <button key={s.key} onClick={() => copyUrl(s.key)} data-testid={`share-${s.key}`}
          className={`ticker text-[10px] px-2 py-1 hover:opacity-80 ${s.color}`}>
          {s.label}
        </button>
      ))}
    </div>
  );
}

function HotAlerts() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/inscriptions/hot-alerts").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data || !data.total) return null;
  return (
    <div className="hard-border bg-impact-red/5 border-impact-red/30 p-4 mb-6" data-testid="hot-alerts-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="ticker text-xs text-impact-red">🔥 FOYERS CHAUDS SIGNALÉS · À RAPPELER</span>
        <span className="text-xs text-muted-foreground">{data.converted}/{data.total} convertis</span>
      </div>
      <div className="space-y-1.5">
        {data.alerts.map((a) => (
          <div key={a.id} className="flex items-center justify-between text-sm border-b border-dashed pb-1.5" data-testid={`hot-alert-${a.id}`}>
            <div className="min-w-0">
              <span className="font-semibold">{a.full_name}</span>
              <span className="text-muted-foreground text-xs"> · {a.members} membres · 📞 {a.phone || "—"}</span>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-impact-gold font-bold">{a.total.toFixed(0)}$</span>
              {a.status === "converti"
                ? <span className="ticker text-[10px] bg-emerald-500/15 text-emerald-600 px-1.5 py-0.5">✓ CONVERTI</span>
                : <span className="ticker text-[10px] bg-impact-red/15 text-impact-red px-1.5 py-0.5">À RAPPELER</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ConversionStats({ stats }) {
  const c = stats.conversion || { soumises: 0, checkout_demarre: 0, payees: 0, en_attente: 0 };
  const pct = (n) => (c.soumises > 0 ? Math.round((n / c.soumises) * 100) : 0);
  const funnel = [
    { key: "soumises", label: "Inscriptions soumises", value: c.soumises, icon: Send, color: "text-blue-600", bar: "bg-blue-500" },
    { key: "checkout", label: "Paiement démarré (Stripe)", value: c.checkout_demarre, icon: CreditCard, color: "text-amber-600", bar: "bg-amber-500" },
    { key: "payees", label: "Payées / activées", value: c.payees, icon: Wallet, color: "text-emerald-600", bar: "bg-emerald-500" },
    { key: "attente", label: "En attente d'action", value: c.en_attente, icon: Clock, color: "text-impact-red", bar: "bg-impact-red" },
  ];
  return (
    <div className="hard-border bg-secondary/20 p-5 mb-6" data-testid="conversion-stats-panel">
      <div className="flex items-center gap-2 mb-4">
        <TrendingUp size={18} className="text-impact-gold"/>
        <h2 className="ticker text-impact-gold">SUIVI DES VENTES · ENTONNOIR DE CONVERSION</h2>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
        {funnel.map((f) => {
          const Icon = f.icon;
          return (
            <div key={f.key} className="hard-border bg-white p-4" data-testid={`conv-${f.key}`}>
              <div className="flex items-center justify-between">
                <Icon size={16} className={f.color}/>
                {f.key !== "soumises" && <span className="text-[10px] font-bold text-muted-foreground">{pct(f.value)}%</span>}
              </div>
              <div className="font-display font-black text-3xl mt-2">{f.value}</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">{f.label}</div>
            </div>
          );
        })}
      </div>
      {stats.family && (stats.family.with_members > 0 || stats.family.total_savings > 0) && (
        <div className="hard-border bg-impact-gold/5 border-impact-gold/30 p-4 mb-5" data-testid="family-summary">
          <div className="flex items-center gap-2 mb-3">
            <UserPlus size={15} className="text-impact-gold"/>
            <span className="ticker text-xs text-impact-gold">RÉSUMÉ FOYER · RABAIS FAMILLE</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            <div data-testid="family-savings">
              <div className="font-display font-black text-3xl text-impact-gold">−{(stats.family.total_savings || 0).toFixed(2)}$</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Économies famille cumulées / 4 sem.</div>
            </div>
            <div data-testid="family-with-members">
              <div className="font-display font-black text-3xl">{stats.family.with_members || 0}</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Inscriptions multi-membres</div>
            </div>
            <div data-testid="family-revenue">
              <div className="font-display font-black text-3xl">{(stats.family.total_revenue || 0).toFixed(0)}$</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Revenu foyers (net) / 4 sem.</div>
            </div>
            <div data-testid="family-reminders-sent">
              <div className="font-display font-black text-3xl text-blue-500">{stats.family.reminders_sent || 0}</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Relances foyer envoyées</div>
            </div>
            <div data-testid="family-reminders-converted">
              <div className="font-display font-black text-3xl text-emerald-600">{stats.family.reminders_converted || 0}</div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Relances converties (payées)</div>
            </div>
            <div data-testid="family-reminder-rate">
              <div className="font-display font-black text-3xl">
                {stats.family.reminders_sent ? Math.round((stats.family.reminders_converted / stats.family.reminders_sent) * 100) : 0}%
              </div>
              <div className="ticker text-[10px] text-muted-foreground mt-1">Taux de conversion relance</div>
            </div>
          </div>
        </div>
      )}
      {stats.by_programme?.length > 0 && (
        <div>
          <div className="ticker text-xs text-muted-foreground mb-2">VENTES PAR PROGRAMME</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            {stats.by_programme.map((p) => (
              <div key={p.programme} className="flex items-center justify-between text-sm py-1 border-b border-dashed" data-testid={`prog-stat-${p.programme}`}>
                <span className="text-xs">{p.programme}</span>
                <span className="font-bold text-impact-gold">{p.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function SourceStats({ stats }) {
  return (
    <div className="hard-border bg-secondary/20 p-5 mb-6" data-testid="stats-panel">
      <div className="flex items-center gap-2 mb-4">
        <BarChart3 size={18} className="text-impact-gold"/>
        <h2 className="ticker text-impact-gold">SOURCES D'INSCRIPTION · {stats.total} AU TOTAL</h2>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="ticker text-xs text-muted-foreground mb-2">PAR SOURCE</div>
          {stats.by_source.length === 0 ? <p className="text-xs italic">—</p> : stats.by_source.map((s) => (
            <div key={s.source} className="flex items-center justify-between text-sm py-1 border-b border-dashed last:border-0" data-testid={`src-${s.source}`}>
              <span className="font-mono text-xs">{s.source}</span>
              <span className="font-bold text-impact-gold">{s.count}</span>
            </div>
          ))}
        </div>
        <div>
          <div className="ticker text-xs text-muted-foreground mb-2">PAR STATUT</div>
          <div className="flex items-center justify-between text-sm py-1 border-b border-dashed">
            <span>En attente</span><span className="font-bold text-impact-red">{stats.by_status.en_attente || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm py-1 border-b border-dashed">
            <span>Approuvés</span><span className="font-bold text-[#10b981]">{stats.by_status.approved || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm py-1">
            <span>Rejetés</span><span className="font-bold text-muted-foreground">{stats.by_status.rejected || 0}</span>
          </div>
        </div>
        <div>
          <div className="ticker text-xs text-muted-foreground mb-2">PAR DISCIPLINE</div>
          {stats.by_discipline.length === 0 ? <p className="text-xs italic">—</p> : stats.by_discipline.map((d) => (
            <div key={d.discipline} className="flex items-center justify-between text-sm py-1 border-b border-dashed last:border-0">
              <span className="text-xs">{d.discipline?.replace(/_/g, " ")}</span>
              <span className="font-bold">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
